/**
 * findReplace — apply an accepted `edit_sheet` proposal to the LIVE editor
 * (T-025, Fase 1). The AI never writes the document server-side; this is the
 * client-side application step the confirmation card triggers.
 *
 * Walks the editor's document text nodes, finds matches of `find` that fit
 * WITHIN a single text node (matches never span node boundaries — mirrors the
 * backend `edit_sheet` proposal), and builds ONE transaction that replaces
 * either every match (`replaceAll`) or the nth 1-based `occurrence`. The marks
 * (bold, color, font, …) of each replaced range are preserved by applying the
 * match node's own marks to the inserted text.
 *
 * Pure-ish and headless-testable: it only touches `editor.state` and
 * `editor.view.dispatch`, so a minimal shim over a real prosemirror-state
 * EditorState exercises it exactly like the running editor (see
 * findReplace.test.js, following the cellSelectionSerializer.test.js pattern).
 *
 * @param {object} editor - TipTap editor (or a `{ state, view }` shim).
 * @param {object} opts
 * @param {string} opts.find - exact text to find (empty -> no-op).
 * @param {string} opts.replace - replacement text (empty -> deletes the match).
 * @param {boolean} [opts.replaceAll=false] - replace every match vs. one.
 * @param {number} [opts.occurrence=1] - 1-based match to replace when not all.
 * @returns {number} how many matches were replaced.
 */
export function findReplace(editor, { find, replace, replaceAll = false, occurrence = 1 }) {
  if (!editor || !find) return 0;

  const { state } = editor;
  const { doc, schema } = state;

  // 1. Collect every match, each fully inside one text node. For a text node,
  //    `pos` is the position just before its first character, so the character
  //    at string index `i` sits at absolute position `pos + i`.
  const matches = [];
  doc.descendants((node, pos) => {
    if (!node.isText || !node.text) return;
    const text = node.text;
    let start = 0;
    let i;
    while ((i = text.indexOf(find, start)) !== -1) {
      matches.push({ from: pos + i, to: pos + i + find.length, marks: node.marks });
      start = i + find.length;
    }
  });

  if (matches.length === 0) return 0;

  // 2. Pick the target matches.
  let targets;
  if (replaceAll) {
    targets = matches;
  } else {
    const idx = Math.max(1, occurrence) - 1;
    if (idx >= matches.length) return 0;
    targets = [matches[idx]];
  }

  // 3. One transaction. Apply from last match to first so earlier positions are
  //    not shifted by earlier replacements.
  const tr = state.tr;
  for (let k = targets.length - 1; k >= 0; k--) {
    const { from, to, marks } = targets[k];
    if (replace) {
      tr.replaceWith(from, to, schema.text(replace, marks));
    } else {
      tr.delete(from, to);
    }
  }

  editor.view.dispatch(tr);
  return targets.length;
}

export default findReplace;
